import React, { createContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';

import { useAuth } from 'contexts/auth';
import courseRequests from 'services/course';
import theme from 'shell/themes';

const defaultForm = {
    firstName: '',
    lastName: '',
    preferredName: '',
    country: null,
    gender: null,
    englishProficiency: null,
    timezone: null,
};

const defaultErrors = {
    firstName: '',
    lastName: '',
};

const PAGES = {
    NAME: 'enter-email',
    DETAILS: 'details',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const { auth, updateCurrentUser } = useAuth();

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultErrors);
    const [currentStep, setCurrentStep] = useState(PAGES.NAME);

    const handleNext = (step) => {
        if (!validateAll()) return;
        setCurrentStep(step);
    };

    const handleComplete = async ({ skip = false }) => {
        setLoading(true);
        let updateUserObj = {
            display_name: form.preferredName || form.firstName || '',
            first_name: form.firstName,
            last_name: form.lastName,
            profile_circle: {
                bg_color: theme.palette.ensightful.error[800],
                text_color: theme.palette.ensightful.shades[0],
            },
            profile_set_up: true,
        };
        if (!skip) {
            updateUserObj = {
                ...updateUserObj,
                gender: form.gender?.key || '',
                country: form.country?.key || '',
                timezone: form.timezone?.key || '',
                english_proficiency: form.englishProficiency?.key || '',
            };
        }
        const updateUser = await updateCurrentUser(updateUserObj);
        const existingCourseData = await courseRequests.findCourse(auth?.course_id);
        const groupFormation = await courseRequests.listCourseGroupFormations(auth?.course_id);
        setLoading(false);
        if (updateUser) {
            if (
                process.env.REACT_APP_AUTH === 'LTI' &&
                auth.role !== 'student' &&
                !existingCourseData?.finished_course_set_up &&
                existingCourseData?.created_on > new Date('2021-12-01').getTime()
            )
                return history.push('/course-set-up');

            if (
                auth.role === 'student' &&
                !auth.group_id &&
                groupFormation &&
                !existingCourseData.finished_group_generation
            ) {
                return history.push('/survey');
            }
            if (!auth.group_id && auth.role === 'student') {
                return history.push('/lti-incomplete');
            }
            return history.push('/');
        } else return history.push('/profile-setup');
    };

    const handleChange = async (key, data) => {
        switch (key) {
            case 'firstName': {
                return setForm((prev) => ({ ...prev, firstName: data }));
            }
            case 'lastName': {
                return setForm((prev) => ({ ...prev, lastName: data }));
            }
            case 'preferredName': {
                return setForm((prev) => ({ ...prev, preferredName: data }));
            }
            case 'gender': {
                return setForm((prev) => ({ ...prev, gender: data }));
            }
            case 'englishProficiency': {
                return setForm((prev) => ({ ...prev, englishProficiency: data }));
            }
            case 'country': {
                return setForm((prev) => ({ ...prev, country: data }));
            }
            case 'timezone': {
                return setForm((prev) => ({ ...prev, timezone: data }));
            }
            default:
                return;
        }
    };

    const getErrorMessage = (key, value) => {
        value = value?.trim();
        switch (key) {
            case 'firstName':
                return value ? '' : 'Please enter a first name.';
            case 'lastName':
                return value ? '' : 'Please enter a last name.';
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors)
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key]);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                errors,
                currentStep,

                handleChange,
                handleNext,
                handleComplete,
                setErrors,
                setForm,
                PAGES,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
